import { getInputMinValueErrorText, getIntegerField, ValidationError } from '@lender/utils';
import { getCurrentEntityPendingChanges } from 'app/pages/loanApplication/LoanApplication.utils';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import {
  EmploymentOwnershipType,
  EmploymentVerificationType,
  extendedIncomeTypes,
  IncomeType,
  SalaryType,
} from 'constants/loanApplication';
import { salaryMap } from 'constants/options';
import {
  HistoryPendingChangesActionType,
  HistoryTypeToPendingChangesTypeMap,
  PendingChangesActionType,
} from 'constants/pendingChanges';
import {
  getConditionalObject,
  normalizeRequestPhoneNumber,
  normalizeViewPhoneNumber,
  replaceEmptyWithNull,
  replaceFalsyValuesWithInitialValues,
} from 'utils/data';
import { formatCalendarDate } from 'utils/date';
import {
  checkBooleanPendingChangeVisibility,
  fillPendingChangesWithActionType,
  formatDatePendingChange,
  formatPhoneNumberPendingChange,
  getApplicableAddressFieldsPendingChanges,
  mergePendingChanges,
  replaceNestedFieldsActionTypeToModified,
} from 'utils/pendingChanges';

import { NEW_RECORD_DATA } from '../../../../FinancialInfo.constants';
import { mapFieldsToFalse } from '../SectionContent.utils';

import { getSalaryTypeModel } from './EmploymentAndIncome.config';
import {
  EMPLOYMENT_VERIFICATION_NON_PENDING_FIELDS,
  employmentVerificationFieldsToExcludeOnSave,
  EmploymentVerificationTableColumnId,
} from './EmploymentAndIncome.constants';

export const salaryIndexedMap = new Map(
  Object.values(SalaryType).map((name, index) => [name, { index, name }]),
);

const getEmploymentOwnershipTypeToSend = (value, incomeType) => {
  switch (true) {
    case value:
      return incomeType === IncomeType.W2
        ? EmploymentOwnershipType.SHARE_LESS_THAN_25_PERCENTS
        : EmploymentOwnershipType.SHARE_GREATER_THAN_OR_EQUAL_TO_25_PERCENT;

    case !value && incomeType === IncomeType.SELF_EMPLOYED:
      return EmploymentOwnershipType.SHARE_LESS_THAN_25_PERCENTS;

    default:
      return null;
  }
};

const formattedKeys = {
  businessName: 'employerName',
  isOwnershipShareLessThan25Percent: 'employmentOwnershipType',
  employerPhone: 'workPhoneNumber',
  address: 'workAddress',
  previousAddress: 'previousWorkAddress',
  jobTitle: 'positionOrTitle',
  type: 'incomeType',
  isForeign: 'foreign',
  isSeasonal: 'seasonal',
};

const booleanFields = [
  'seasonal',
  'isCorporateRelocation',
  'isEmployeeLoan',
  'foreign',
  'isEmployedByPartyToTransaction',
  'isCurrentlyEmployedHere',
  'employmentOwnershipType',
];

const defaultBooleanValues = mapFieldsToFalse(booleanFields);

const phoneFields = ['workPhoneNumber', 'employerPhone', 'previousWorkPhoneNumber'];

const extractAddressFields = value => {
  const {
    city,
    county,
    street,
    unit,
    country: {
      state: { abbreviation: state },
      zip,
    },
  } = value.address;

  return {
    city,
    county,
    street,
    unit,
    state,
    zip,
  };
};

export const normalizeReceivedIncomesData = data =>
  Object.entries(data).reduce(
    (acc, [id, { incomeType, borrowerId: owner }]) => {
      const rawIncomeData = {};

      const formattedIncomeData = Object.entries(incomeType).reduce(
        (incomeTypeAcc, [key, value]) => {
          const formattedKey = formattedKeys[key] ?? key;
          const isExtendedIncomeType = extendedIncomeTypes.includes(incomeType.type);

          acc.rawData[id] = {
            ...acc.rawData[id],
            [formattedKey]: value,
          };

          switch (true) {
            case key === 'monthlyIncome':
              if (extendedIncomeTypes.includes(incomeType.type)) {
                const salaryOption = salaryIndexedMap.get(SalaryType.BASE_SALARY);

                incomeTypeAcc.initialSalaryFields = (incomeTypeAcc.initialSalaryFields ?? 0) + 1;

                incomeTypeAcc[SalaryType.BASE_SALARY] = value;
                incomeTypeAcc.salary = {
                  ...incomeTypeAcc.salary,
                  [getSalaryTypeModel(salaryOption.index)]: {
                    input: salaryOption.name,
                    value: value || null,
                    index: salaryOption.index,
                  },
                };
                break;
              }

              incomeTypeAcc[formattedKey] = value;
              break;

            case key === 'employmentVerifications':
              if (extendedIncomeTypes.includes(incomeType.type)) {
                const normalizedValue = value.map(employmentVerification => {
                  return {
                    ...employmentVerification,
                    hireDate: employmentVerification.hireDate
                      ? formatCalendarDate(
                          employmentVerification.hireDate,
                          COMMON_REQUEST_DATE_FORMAT,
                        )
                      : employmentVerification.hireDate,
                    dateCompleted: employmentVerification.dateCompleted
                      ? formatCalendarDate(
                          employmentVerification.dateCompleted,
                          COMMON_REQUEST_DATE_FORMAT,
                        )
                      : employmentVerification.dateCompleted,
                  };
                });

                incomeTypeAcc[formattedKey] = normalizedValue;
                break;
              }

              incomeTypeAcc[formattedKey] = value;
              break;

            case key === 'bonuses':
              incomeTypeAcc.salary = {
                ...incomeTypeAcc.salary,
                ...Object.entries(value).reduce((salaryAcc, [salaryKey, value]) => {
                  const salaryOption = salaryIndexedMap.get(salaryKey);

                  if (salaryOption) {
                    salaryAcc[getSalaryTypeModel(salaryOption.index)] = {
                      input: salaryOption.name,
                      value,
                      index: salaryOption.index,
                    };

                    incomeTypeAcc.initialSalaryFields =
                      (incomeTypeAcc.initialSalaryFields ?? 0) + 1;
                  }

                  incomeTypeAcc[salaryKey] = value;

                  return salaryAcc;
                }, {}),
              };
              break;

            case key === 'status':
              {
                const { endDate, startDate, type } = value;

                incomeTypeAcc.startDate = startDate;
                incomeTypeAcc.endDate = endDate ?? null;
                incomeTypeAcc.isCurrentlyEmployedHere =
                  type === 'Current' && isExtendedIncomeType ? true : false;
                rawIncomeData.startDate = startDate;
                rawIncomeData.endDate = endDate;
                rawIncomeData.isCurrentlyEmployedHere =
                  type === 'Current' && isExtendedIncomeType ? true : false;
              }

              break;

            case phoneFields.includes(key):
              incomeTypeAcc[formattedKey] = normalizeViewPhoneNumber(value);
              rawIncomeData[formattedKey] = value;

              break;

            case key === 'businessName':
              incomeTypeAcc[formattedKey] = value;
              rawIncomeData[formattedKey] = value;

              break;

            case key === 'isOwnershipShareLessThan25Percent':
              incomeTypeAcc[formattedKey] = !!value;
              rawIncomeData[formattedKey] = value;
              break;

            case key === 'address':
              incomeTypeAcc[formattedKey] = extractAddressFields(value);
              rawIncomeData[formattedKey] = value;
              break;

            case key === 'previousAddress':
              incomeTypeAcc[formattedKey] = extractAddressFields(value);
              incomeTypeAcc.isPreviousWorkAddressExist = !!value;
              rawIncomeData[formattedKey] = value;
              rawIncomeData.isPreviousWorkAddressExist = !!value;
              break;

            case key === 'businessOwnershipInfo':
              {
                const taxFilingType = _.get(value, 'taxFilingType.type', null);
                const titlePerTaxReturn = _.get(value, 'taxFilingType.titlePerTaxReturn', null);
                const companyEin = _.get(value, 'ein', null);

                incomeTypeAcc.taxFilingType = taxFilingType;
                incomeTypeAcc.titlePerTaxReturn = titlePerTaxReturn;
                incomeTypeAcc.companyEin = companyEin;
                incomeTypeAcc.employmentOwnershipType = true;
                rawIncomeData.taxFilingType = taxFilingType;
                rawIncomeData.titlePerTaxReturn = titlePerTaxReturn;
                rawIncomeData.companyEin = companyEin;
                rawIncomeData.employmentOwnershipType = true;
              }

              break;

            case key.startsWith('is') && _.isNull(value):
              incomeTypeAcc[formattedKey] = false;
              rawIncomeData[formattedKey] = value;
              break;

            case key === 'yearsInProfession' || key === 'monthsInProfession':
              incomeTypeAcc[formattedKey] = value || null;
              rawIncomeData[formattedKey] = value;
              break;

            default:
              incomeTypeAcc[formattedKey] = value;
              rawIncomeData[formattedKey] = value;
          }

          return incomeTypeAcc;
        },
        {
          ...defaultBooleanValues,
        },
      );

      acc.formattedData[id] = {
        ...formattedIncomeData,
        owner,
        isSalaryChanged: false,
      };
      acc.rawData[id] = rawIncomeData;

      return acc;
    },
    { formattedData: {}, rawData: {} },
  );

const getFormattedAddress = address =>
  replaceEmptyWithNull({
    ...address,
    county: address?.county?.value,
    state: address?.state?.value,
    country: 'USA',
  });

const getEmploymentVerificationsBulkUpdate = ({
  isExtendedIncomeType,
  entitiesSideData,
  recordId,
}) => {
  if (!isExtendedIncomeType) {
    return null;
  }

  const employmentVerifications = entitiesSideData[recordId]?.employmentVerifications;

  if (!employmentVerifications) {
    return {
      create: [],
      update: {},
      delete: [],
    };
  }

  return employmentVerifications.reduce(
    (acc, employmentVerification) => {
      const { isDeleted, isAdded } = employmentVerification;
      const employmentVerificationToSave = _.omit(
        employmentVerification,
        employmentVerificationFieldsToExcludeOnSave,
      );

      if (isDeleted) {
        acc.delete.push(employmentVerificationToSave.id);

        return acc;
      }

      if (isAdded) {
        acc.create.push(_.omit(employmentVerificationToSave, 'id'));

        return acc;
      }

      acc.update[employmentVerificationToSave.id] = employmentVerificationToSave;

      return acc;
    },
    {
      create: [],
      update: {},
      delete: [],
    },
  );
};

export const normalizeIncomesDataToSend = ({
  deletedRecords,
  data,
  rawData,
  dataToNormalize,
  entitiesSideData,
}) => {
  const { normalizedDataToUpdate, normalizedDataToCreate } = Object.entries(dataToNormalize).reduce(
    (acc, [recordId, recordData]) => {
      const {
        incomeType,
        employerName,
        positionOrTitle,
        startDate,
        endDate,
        salary,
        workAddress,
        isPreviousWorkAddressExist,
        previousWorkAddress,
        previousWorkPhoneNumber,
        monthlyIncome,
        grossMonthlyBaseSalary,
        workPhoneNumber,
        employmentOwnershipType,
        taxFilingType,
        titlePerTaxReturn,
        isCorporateRelocation,
        isEmployeeLoan,
        foreign,
        seasonal,
        isEmployedByPartyToTransaction,
        isCurrentlyEmployedHere,
        yearsInProfession,
        monthsInProfession,
        owner,
        ...restRecordData
      } = recordData;

      const isExtendedIncomeType = extendedIncomeTypes.includes(incomeType?.value);

      if (deletedRecords.has(recordId)) {
        return acc;
      }

      const salaryTypeValues = Object.values(SalaryType);
      const getFormattedSalaryForm = () =>
        Object.values(salary ?? {}).reduce((acc, { input, value }) => {
          const salaryType = input.value;

          acc[salaryType] = value;

          return acc;
        }, {});

      const getFormattedSalaryFields = salaryForm =>
        salaryTypeValues.reduce((acc, value) => {
          acc[value] = salaryForm[value] ?? null;

          return acc;
        }, {});

      const employmentVerificationsBulkUpdate = getEmploymentVerificationsBulkUpdate({
        isExtendedIncomeType,
        entitiesSideData,
        recordId,
      });

      const normalizedRecordData = {
        incomeType: incomeType?.value,
        startDate: startDate ? dayjs(startDate).format(COMMON_REQUEST_DATE_FORMAT) : undefined,
        ...(extendedIncomeTypes.includes(incomeType?.value)
          ? {
              employerName,
              positionOrTitle,
              workPhoneNumber: normalizeRequestPhoneNumber(workPhoneNumber),
              ...(incomeType?.value === IncomeType.W2
                ? getFormattedSalaryFields(getFormattedSalaryForm())
                : {
                    grossMonthlyBaseSalary,
                  }),
              workAddress: getFormattedAddress(workAddress),
              ...getConditionalObject(isPreviousWorkAddressExist, () => ({
                previousWorkAddress: getFormattedAddress(previousWorkAddress),
                previousWorkPhoneNumber: normalizeRequestPhoneNumber(previousWorkPhoneNumber),
              })),
              ...getConditionalObject(!isCurrentlyEmployedHere, () => ({
                endDate: dayjs(endDate).format(COMMON_REQUEST_DATE_FORMAT),
              })),
              employmentOwnershipType: getEmploymentOwnershipTypeToSend(
                employmentOwnershipType,
                incomeType?.value,
              ),
              ...replaceEmptyWithNull({
                taxFilingType: taxFilingType?.value,
                titlePerTaxReturn: titlePerTaxReturn?.value,
              }),
              ...replaceFalsyValuesWithInitialValues(
                {
                  foreign,
                  seasonal,
                  ...getConditionalObject(incomeType?.value === IncomeType.W2, {
                    isCorporateRelocation,
                    isEmployeeLoan,
                    isEmployedByPartyToTransaction,
                  }),
                  isCurrentlyEmployedHere,
                },
                rawData[recordId],
              ),
              yearsInProfession: yearsInProfession || 0,
              monthsInProfession: monthsInProfession || 0,
              employmentVerificationsBulkUpdate,
              ...restRecordData,
            }
          : { monthlyIncome, employmentVerificationsBulkUpdate }),
      };

      if (recordId.startsWith(NEW_RECORD_DATA.id)) {
        const ownerId = owner.value;

        acc.normalizedDataToCreate[ownerId] = [
          ...(acc.normalizedDataToCreate[ownerId] ?? []),
          normalizedRecordData,
        ];
      }

      if (!recordId.startsWith(NEW_RECORD_DATA.id)) {
        const { owner } = data[recordId];

        acc.normalizedDataToUpdate[owner] = {
          ...acc.normalizedDataToUpdate[owner],
          [recordId]: normalizedRecordData,
        };
      }

      return acc;
    },
    {
      normalizedDataToUpdate: {},
      normalizedDataToCreate: {},
    },
  );

  return {
    create: normalizedDataToCreate,
    update: normalizedDataToUpdate,
    delete: Array.from(deletedRecords),
  };
};

const getBooleanEmploymentOwnershipType = (value, incomeType) => {
  switch (value) {
    case EmploymentOwnershipType.SHARE_LESS_THAN_25_PERCENTS:
      return incomeType === IncomeType.W2 ? true : false;

    case EmploymentOwnershipType.SHARE_GREATER_THAN_OR_EQUAL_TO_25_PERCENT:
      return true;

    default:
      return !!value;
  }
};

const initialPendingChangesFormattedKeys = {
  address: 'workAddress',
  previousAddress: 'previousWorkAddress',
};

const initialPendingChangesAddressFields = ['address', 'previousAddress'];

const initialPendingChangesPhoneFields = ['workPhoneNumber', 'previousWorkPhoneNumber'];

const excludedInitialPendingChangesFields = ['workAddressId', 'previousWorkAddressId'];

const normalizeInitialPendingChanges = ({ pendingChanges, data, viewData }) => {
  return Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    const { newValue: incomeTypeNewValue, initialValue: incomeTypeInitialValue } =
      recordPendingChanges.incomeType ?? {};

    const incomeTypeFromData = viewData?.[recordId]?.incomeType ?? data?.[recordId]?.incomeType;

    const formattedRecordPendingChanges = Object.entries(recordPendingChanges).reduce(
      (acc, [key, value]) => {
        const formattedKey = initialPendingChangesFormattedKeys[key] ?? key;

        switch (true) {
          case excludedInitialPendingChangesFields.includes(key):
            break;

          case key === 'employmentOwnershipType':
            acc[formattedKey] = {
              newValue: getBooleanEmploymentOwnershipType(
                value.newValue,
                incomeTypeNewValue ?? incomeTypeFromData,
              ),
              initialValue: getBooleanEmploymentOwnershipType(
                value.initialValue,
                incomeTypeInitialValue ?? incomeTypeFromData,
              ),
            };
            break;

          case initialPendingChangesPhoneFields.includes(key):
            acc[formattedKey] = formatPhoneNumberPendingChange(value);
            break;

          case initialPendingChangesAddressFields.includes(key):
            acc[formattedKey] =
              viewData?.[recordId]?.pendingChangesActionType === PendingChangesActionType.ADDED
                ? value
                : replaceNestedFieldsActionTypeToModified(value);
            break;

          default:
            acc[formattedKey] = value;
        }

        return acc;
      },
      {},
    );

    acc[recordId] = formattedRecordPendingChanges;

    return acc;
  }, {});
};

const formatPendingChangesToForm = ({
  pendingChanges,
  data,
  form,
  entitiesSideData,
  initialEntitiesSideData,
}) => {
  return Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    const {
      incomeType,
      startDate,
      endDate,
      salary,
      employmentVerifications,
      ...restRecordPendingChanges
    } = recordPendingChanges;

    const incomeTypeValue = incomeType?.newValue ?? data[recordId]?.incomeType;

    const filteredRestRecordPendingChanges = !extendedIncomeTypes.includes(incomeTypeValue)
      ? restRecordPendingChanges
      : Object.entries(restRecordPendingChanges).reduce(
          (acc, [key, value]) => {
            switch (true) {
              case !!salaryMap[key]:
                {
                  const salaryInForm = form[recordId]?.salary ?? {};
                  const incomeTypeInForm = form[recordId]?.incomeType?.value ?? {};

                  const currentSalaryFieldEntry = Object.entries(salaryInForm).find(
                    ([__, { input }]) => input?.value === key,
                  );

                  if (currentSalaryFieldEntry) {
                    const [salaryKeyInForm, { value: currentSalaryValue }] =
                      currentSalaryFieldEntry;

                    const salaryPendingChange = {
                      newValue: currentSalaryValue,
                      initialValue: value?.initialValue,
                    };

                    _.set(acc, ['salary', salaryKeyInForm, 'value'], salaryPendingChange);
                  }

                  if (incomeTypeInForm !== IncomeType.W2) {
                    acc[key] = value;
                  }
                }

                break;

              case key === 'workAddress' || key === 'previousWorkAddress':
                {
                  const addressPendingChange = getApplicableAddressFieldsPendingChanges(value);

                  acc[key] = addressPendingChange;
                }

                break;

              default:
                acc[key] = value;
            }

            return acc;
          },
          {
            salary,
          },
        );

    const mergedEmploymentVerificationPendingChanges =
      entitiesSideData?.[recordId]?.employmentVerifications?.reduce(
        (acc, employmentVerificationData) => {
          const initialEmploymentVerificationData = initialEntitiesSideData?.[
            recordId
          ]?.employmentVerifications?.find(
            initialEmploymentVerificationData =>
              initialEmploymentVerificationData.id === employmentVerificationData.id,
          );

          const historyPendingChanges =
            employmentVerifications?.[employmentVerificationData.id] ?? {};

          const employmentVerificationsPendingChanges = getCurrentEntityPendingChanges({
            initialValues: initialEmploymentVerificationData ?? employmentVerificationData,
            currentValues: employmentVerificationData,
            actionType: Object.values(historyPendingChanges).some(
              ({ actionType }) => actionType === HistoryPendingChangesActionType.ADDED,
            )
              ? HistoryPendingChangesActionType.ADDED
              : null,
            omittedFields: EMPLOYMENT_VERIFICATION_NON_PENDING_FIELDS,
          });

          const mergedPendingChanges = employmentVerifications?.[employmentVerificationData.id]
            ? mergePendingChanges(historyPendingChanges, employmentVerificationsPendingChanges)
            : employmentVerificationsPendingChanges;

          acc[employmentVerificationData.id] = _.omit(
            mergedPendingChanges,
            EMPLOYMENT_VERIFICATION_NON_PENDING_FIELDS,
          );

          return acc;
        },
        {},
      ) ?? employmentVerifications;

    const formattedRecordPendingChanges = {
      ...filteredRestRecordPendingChanges,
      employmentVerifications: mergedEmploymentVerificationPendingChanges,
      ...getConditionalObject(incomeType, {
        incomeType,
      }),
      ...getConditionalObject(endDate, () => ({
        endDate: formatDatePendingChange(endDate),
      })),
      ...getConditionalObject(startDate, () => ({
        startDate: formatDatePendingChange(startDate),
      })),
    };

    acc[recordId] = formattedRecordPendingChanges;

    return acc;
  }, {});
};

const filterPendingChanges = pendingChanges =>
  Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    const filteredPendingChanges = Object.entries(recordPendingChanges).reduce(
      (filteredPendingChangesAcc, [pendingChangeKey, pendingChangeValue]) => {
        const incomeTypeNewValue = recordPendingChanges.incomeType?.newValue;

        const isIncomeTypeChanged = incomeTypeNewValue !== undefined;

        const getCondition = () => {
          switch (pendingChangeKey) {
            case 'workAddressId':
            case 'previousWorkAddressId':
              return false;

            case 'monthlyIncome':
              return (
                !isIncomeTypeChanged ||
                !extendedIncomeTypes.includes(recordPendingChanges.incomeType?.newValue)
              );

            case 'employerName':
            case 'positionOrTitle':
            case 'grossMonthlyBaseSalary':
            case 'salary':
            case 'workAddress':
            case 'workPhoneNumber':
            case 'previousWorkAddress':
            case 'previousWorkPhoneNumber':
              return !isIncomeTypeChanged || extendedIncomeTypes.includes(incomeTypeNewValue);

            case 'seasonal':
            case 'foreign':
            case 'isCurrentlyEmployedHere':
            case 'employmentOwnershipType':
              return (
                (!isIncomeTypeChanged || extendedIncomeTypes.includes(incomeTypeNewValue)) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );

            case 'yearsInProfession':
            case 'monthsInProfession':
              return (
                (!isIncomeTypeChanged || extendedIncomeTypes.includes(incomeTypeNewValue)) &&
                (Number(pendingChangeValue?.newValue) || 0) !==
                  (Number(pendingChangeValue?.initialValue) || 0)
              );

            case 'endDate':
              return (
                (!isIncomeTypeChanged || extendedIncomeTypes.includes(incomeTypeNewValue)) &&
                (recordPendingChanges.isCurrentlyEmployedHere?.newValue === undefined ||
                  recordPendingChanges.isCurrentlyEmployedHere.newValue === false)
              );

            case 'isEmployedByPartyToTransaction':
            case 'isCorporateRelocation':
            case 'isEmployeeLoan':
              return (
                (!isIncomeTypeChanged || incomeTypeNewValue === IncomeType.W2) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );

            case 'taxFilingType':
            case 'titlePerTaxReturn':
            case 'companyEin':
              return (
                (!isIncomeTypeChanged || incomeTypeNewValue === IncomeType.SELF_EMPLOYED) &&
                (recordPendingChanges.employmentOwnershipType?.newValue === undefined ||
                  getBooleanEmploymentOwnershipType(
                    recordPendingChanges.employmentOwnershipType.newValue,
                    IncomeType.SELF_EMPLOYED,
                  ))
              );

            case SalaryType.BASE_SALARY:
              return (
                !isIncomeTypeChanged ||
                extendedIncomeTypes.includes(recordPendingChanges.incomeType?.newValue)
              );

            case salaryMap[pendingChangeKey]?.value:
              return (
                !isIncomeTypeChanged || recordPendingChanges.incomeType?.newValue === IncomeType.W2
              );

            default:
              return true;
          }
        };

        const condition = getCondition();

        if (condition) {
          filteredPendingChangesAcc[pendingChangeKey] = pendingChangeValue;
        }

        return filteredPendingChangesAcc;
      },
      {},
    );

    acc[recordId] = filteredPendingChanges;

    return acc;
  }, {});

export const normalizeIncomesPendingChanges = ({
  initialPendingChanges,
  formPendingChanges = {},
  data,
  viewData,
  form,
  isEditFlow = false,
  entitiesSideData,
  initialEntitiesSideData,
  newEntities,
  deletedEntities,
}) => {
  const normalizedInitialPendingChanges = normalizeInitialPendingChanges({
    pendingChanges: initialPendingChanges,
    viewData,
    data,
  });

  const mergedPendingChanges = mergePendingChanges(
    normalizedInitialPendingChanges,
    formPendingChanges,
  );

  const filteredPendingChanges = filterPendingChanges(mergedPendingChanges, isEditFlow);

  const dataToReturn = isEditFlow
    ? formatPendingChangesToForm({
        pendingChanges: filteredPendingChanges,
        data,
        form,
        entitiesSideData,
        initialEntitiesSideData,
      })
    : filteredPendingChanges;

  return fillPendingChangesWithActionType({
    pendingChanges: dataToReturn,
    newRecords: newEntities,
    deletedRecords: deletedEntities,
  });
};

export const getExperienceFieldSchema = (isRequired, relatedFieldNameInModel) => {
  return getIntegerField({ isRequired }).test(function (value) {
    if (!isRequired) {
      return true;
    }

    const relatedFieldValue = _.get(this.options.context, relatedFieldNameInModel);

    if (value === 0 && relatedFieldValue === 0) {
      return this.createError({
        message: getInputMinValueErrorText(1, false, ValidationError.MinValue),
      });
    }

    return true;
  });
};

export const orderEmploymentVerification = ({ employmentVerifications, order }) =>
  _.orderBy(
    employmentVerifications,
    [
      employmentVerification => {
        const value = _.get(employmentVerification, order.id);

        switch (order.id) {
          case EmploymentVerificationTableColumnId.ON_TEMPORARY_LEAVE:
            return value ? 'Yes' : 'No';

          default:
            return _.toLower(value);
        }
      },
    ],
    [order.direction],
  );

export const createNewEmploymentVerificationData = ({ verificationType } = {}) => {
  return {
    id: _.uniqueId(),
    // * This flag is needed for cases when we add new entity but not save it
    isNewlyAdded: true,
    isAdded: true,
    verificationType: verificationType ?? EmploymentVerificationType.STANDARD,
  };
};

export const prepareModifiedFields = ({ currentData, pendingChangesData, initialValuesMap }) =>
  currentData.map(values => {
    const pendingChanges = pendingChangesData[values.id];
    const currentPendingChanges = getCurrentEntityPendingChanges({
      initialValues: initialValuesMap[values.id],
      currentValues: values,
      omittedFields: EMPLOYMENT_VERIFICATION_NON_PENDING_FIELDS,
    });

    if (!pendingChanges && _.isEmpty(currentPendingChanges)) {
      return {
        ...values,
        pendingChangesActionType: PendingChangesActionType.NO_CHANGES,
      };
    }

    const mergedPendingChanges = _.omit(
      mergePendingChanges(pendingChanges, currentPendingChanges),
      ['isSeparateChange', ...EMPLOYMENT_VERIFICATION_NON_PENDING_FIELDS],
    );

    const modifiedFields = Object.keys(mergedPendingChanges);

    const normalizedModifiedFields = modifiedFields.reduce((acc, field) => {
      const pendingChange = mergedPendingChanges[field];

      if (pendingChange.newValue === pendingChange.initialValue) {
        return acc;
      }

      acc.push(_.upperFirst(field));

      return acc;
    }, []);

    const historyActionType = modifiedFields[0]
      ? mergedPendingChanges[modifiedFields[0]]?.actionType
      : '';
    const pendingChangesActionType = HistoryTypeToPendingChangesTypeMap[historyActionType];

    return {
      ...values,
      modifiedFields: normalizedModifiedFields,
      pendingChangesActionType,
    };
  });

export const prepareInitialDeletedFlags = ({
  employmentVerifications = [],
  employmentVerificationPendingChangesData,
  isRecordDeleted,
  isRecordNewAdded,
}) =>
  employmentVerifications?.map(employmentVerificationData => {
    const pendingChanges = employmentVerificationPendingChangesData[employmentVerificationData.id];

    if (!pendingChanges) {
      return {
        ...employmentVerificationData,
        isAdded: isRecordNewAdded,
        isDeleted: isRecordDeleted,
      };
    }

    const historyActionType = pendingChanges.employerVerifierName?.actionType;
    const isDeleted = historyActionType === HistoryPendingChangesActionType.DELETED;
    const isAdded = historyActionType === HistoryPendingChangesActionType.ADDED;

    if (!isDeleted) {
      return employmentVerificationData;
    }

    return {
      ...employmentVerificationData,
      isDeleted,
      isAdded,
    };
  });

const unwrapSelectValue = field => (field && 'value' in field ? field.value : field) ?? null;

const formatOptionalDate = value =>
  value ? dayjs(value).format(COMMON_REQUEST_DATE_FORMAT) : null;

export const normalizeEmploymentVerificationFormData = ({ updatedData, initialData }) => {
  return {
    ...updatedData,
    id: initialData.id,
    isAdded: initialData.isAdded,
    verificationType: initialData.verificationType ?? EmploymentVerificationType.STANDARD,
    businessVerificationMethod: unwrapSelectValue(updatedData.businessVerificationMethod),
    employedAtTimeOfVerification: unwrapSelectValue(updatedData.employedAtTimeOfVerification),
    employerPhoneSource: unwrapSelectValue(updatedData.employerPhoneSource),
    probabilityOfContinuedEmployment: unwrapSelectValue(
      updatedData.probabilityOfContinuedEmployment,
    ),
    verificationMethod: unwrapSelectValue(updatedData.verificationMethod),
    comments: updatedData.comments || '',
    hireDate: formatOptionalDate(updatedData.hireDate),
    dateCompleted: formatOptionalDate(updatedData.dateCompleted),
  };
};

export const getMonthlyIncome = ({
  incomeType,
  grossMonthlyBaseSalary,
  grossMonthlyOvertime,
  grossMonthlyBonus,
  grossMonthlyCommission,
  contractBasis,
  militaryBasePay,
  militaryClothesAllowance,
  militaryCombatPay,
  militaryFlightPay,
  militaryHazardPay,
  militaryOverseasPay,
  militaryPropPay,
  militaryQuartersAllowance,
  militaryRationsAllowance,
  militaryVariableHousingAllowance,
  militaryOther,
  monthlyIncome,
  isPastIncomesExcluded,
}) => {
  const monthlyIncomeFieldsForW2Type = [
    grossMonthlyBaseSalary,
    grossMonthlyOvertime,
    grossMonthlyBonus,
    grossMonthlyCommission,
    contractBasis,
    militaryBasePay,
    militaryClothesAllowance,
    militaryCombatPay,
    militaryFlightPay,
    militaryHazardPay,
    militaryOverseasPay,
    militaryPropPay,
    militaryQuartersAllowance,
    militaryRationsAllowance,
    militaryVariableHousingAllowance,
    militaryOther,
  ];

  const getTotalIncome = () => {
    switch (incomeType) {
      case IncomeType.W2:
        return isPastIncomesExcluded
          ? 0
          : monthlyIncomeFieldsForW2Type.reduce((acc, income) => acc + (Number(income) || 0), 0);

      case IncomeType.SELF_EMPLOYED:
        return isPastIncomesExcluded ? 0 : Number(grossMonthlyBaseSalary) || 0;

      default:
        return Number(monthlyIncome) || 0;
    }
  };

  const totalIncome = getTotalIncome();

  return totalIncome;
};
